/**
 * API Service
 * Handles all backend API calls with proper error handling
 */

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000/api';

interface ApiError {
  message: string;
  statusCode?: number;
}

/**
 * Generic API call wrapper with error handling
 */
async function apiCall<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  const defaultOptions: RequestInit = {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    credentials: 'include', // Include HTTP-only cookies
    ...options,
  };

  try {
    const response = await fetch(url, defaultOptions);

    // Parse response body (guard against empty 204/304 responses)
    let data: any = null;
    const hasBody = response.status !== 204 && response.status !== 304;
    if (hasBody) {
      const text = await response.text();
      data = text ? JSON.parse(text) : null;
    }

    // Handle non-2xx responses
    if (!response.ok) {
      throw {
        message: data?.message || 'An error occurred',
        statusCode: response.status,
      } as ApiError;
    }

    return data as T;
  } catch (error: any) {
    // Network error or JSON parse error
    if (!error.statusCode) {
      throw {
        message: 'Network error. Please check your connection.',
        statusCode: 0,
      } as ApiError;
    }
    throw error;
  }
}

/**
 * Authentication API
 */
export const authApi = {
  /**
   * Login user
   */
  async login(email: string, password: string) {
    return apiCall<{
      success: boolean;
      message: string;
      user: {
        _id: string;
        email: string;
        businessName: string;
        industry: string;
        role?: 'USER' | 'ADMIN';
        subscription?: {
          plan: string;
          status: string;
          maxPhoneNumbers: number;
          maxConcurrentCalls: number;
          minutesIncluded: number;
          minutesUsed: number;
          stripeCustomerId?: string;
          stripeSubscriptionId?: string;
          currentPeriodStart?: string;
          currentPeriodEnd?: string;
          cancelAtPeriodEnd: boolean;
        };
        usage?: {
          phoneNumbersUsed: number;
          currentConcurrentCalls: number;
          totalCallsThisMonth: number;
          totalMinutesThisMonth: number;
        };
      };
    }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  },

  /**
   * Register new user
   */
  async register(data: {
    email: string;
    password: string;
    businessName: string;
    industry: 'restaurant' | 'dental' | 'general' | 'medical';
  }) {
    return apiCall<{
      success: boolean;
      message: string;
      user: {
        _id: string;
        email: string;
        businessName: string;
        industry: string;
        role?: 'USER' | 'ADMIN';
        subscription?: {
          plan: string;
          status: string;
          maxPhoneNumbers: number;
          maxConcurrentCalls: number;
          minutesIncluded: number;
          minutesUsed: number;
          stripeCustomerId?: string;
          stripeSubscriptionId?: string;
          currentPeriodStart?: string;
          currentPeriodEnd?: string;
          cancelAtPeriodEnd: boolean;
        };
        usage?: {
          phoneNumbersUsed: number;
          currentConcurrentCalls: number;
          totalCallsThisMonth: number;
          totalMinutesThisMonth: number;
        };
      };
    }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Logout user
   */
  async logout() {
    return apiCall<{
      success: boolean;
      message: string;
    }>('/auth/logout', {
      method: 'POST',
    });
  },

  /**
   * Get current user
   */
  async getCurrentUser() {
    return apiCall<{
      success: boolean;
      user: {
        _id: string;
        email: string;
        businessName: string;
        industry: string;
        role?: 'USER' | 'ADMIN';
        subscription?: {
          plan: string;
          status: string;
          maxPhoneNumbers: number;
          maxConcurrentCalls: number;
          minutesIncluded: number;
          minutesUsed: number;
          stripeCustomerId?: string;
          stripeSubscriptionId?: string;
          currentPeriodStart?: string;
          currentPeriodEnd?: string;
          cancelAtPeriodEnd: boolean;
        };
        usage?: {
          phoneNumbersUsed: number;
          currentConcurrentCalls: number;
          totalCallsThisMonth: number;
          totalMinutesThisMonth: number;
        };
        settings?: {
          notificationEmail?: string;
          notificationPhone?: string;
          notificationPreferences?: {
            enableEmailNotifications: boolean;
            enableSmsAlerts: boolean;
            sendDailyReports: boolean;
            sendWeeklyReports: boolean;
            nexhealthDefaults?: {
              subdomain: string;
              apiKey: string;
              locationId?: number;
            };
          };
          language: string;
        };
      };
    }>('/auth/me', {
      method: 'GET',
    });
  },

  /**
   * Update user profile
   */
  async updateProfile(data: { businessName?: string; email?: string }) {
    return apiCall<{
      success: boolean;
      message: string;
      user: any;
    }>('/auth/profile', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  /**
   * Change password
   */
  async changePassword(data: { currentPassword: string; newPassword: string }) {
    return apiCall<{
      success: boolean;
      message: string;
    }>('/auth/password', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  /**
   * Update notification preferences
   */
  async updateNotifications(data: {
    notificationEmail?: string;
    notificationPhone?: string;
    notificationPreferences?: {
      enableEmailNotifications?: boolean;
      enableSmsAlerts?: boolean;
      sendDailyReports?: boolean;
      sendWeeklyReports?: boolean;
      nexhealthDefaults?: {
        subdomain: string;
        apiKey: string;
        locationId?: number;
      };
    };
  }) {
    return apiCall<{
      success: boolean;
      message: string;
      user: any;
    }>('/auth/notifications', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  /**
   * Update language preference
   */
  async updateLanguage(data: { language: string }) {
    return apiCall<{
      success: boolean;
      message: string;
      user: any;
    }>('/auth/language', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },
};

/**
 * Subscription API
 */
export const subscriptionApi = {
  /**
   * Create Stripe checkout session
   */
  async createCheckout(plan: 'starter' | 'professional' | 'enterprise') {
    return apiCall<{
      success: boolean;
      sessionId: string;
      url: string;
    }>('/subscription/create-checkout', {
      method: 'POST',
      body: JSON.stringify({ plan }),
    });
  },

  /**
   * Get current subscription
   */
  async getCurrentSubscription() {
    return apiCall<{
      success: boolean;
      subscription: {
        plan: string;
        status: string;
        maxPhoneNumbers: number;
        maxConcurrentCalls: number;
        minutesIncluded: number;
        minutesUsed: number;
        currentPeriodEnd?: string;
        cancelAtPeriodEnd: boolean;
      };
    }>('/subscription/current', {
      method: 'GET',
    });
  },

  /**
   * Cancel subscription
   */
  async cancelSubscription() {
    return apiCall<{
      success: boolean;
      message: string;
    }>('/subscription/cancel', {
      method: 'POST',
    });
  },

  /**
   * Create customer portal session
   */
  async createPortalSession() {
    return apiCall<{
      success: boolean;
      url: string;
    }>('/subscription/portal', {
      method: 'POST',
    });
  },
};

/**
 * Agent API
 */
export const agentApi = {
  /**
   * Create a new agent (full lifecycle: serial assign, phone purchase, LiveKit setup)
   */
  async createAgent(data: {
    name: string;
    knowledgeBase: Record<string, any>;
    personalityPrompt: string;
    phoneNumber: string;
    settings: {
      language: string;
      greetingMessage: string;
      escalationRules?: string;
      maxCallDurationMinutes: number;
      transferPhone?: string;
      nexhealth?: { enabled: boolean; subdomain: string; apiKey: string; locationId?: number };
      ragEnabled?: boolean;
    };
  }) {
    return apiCall<{
      _id: string;
      name: string;
      phoneNumber: string;
      status: string;
    }>('/agent', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * List all active agents for the authenticated user
   */
  async listAgents() {
    return apiCall<Array<{
      _id: string;
      name: string;
      phoneNumber: string;
      industry: string;
      status: string;
      settings: {
        language: string;
        greetingMessage: string;
        escalationRules: string;
        maxCallDurationMinutes: number;
      };
      createdAt: string;
    }>>('/agent', {
      method: 'GET',
    });
  },

  /**
   * Get a single agent by ID
   */
  async getAgent(agentId: string) {
    return apiCall<{
      _id: string;
      name: string;
      phoneNumber: string;
      industry: string;
      personalityPrompt: string;
      settings: {
        language: string;
        greetingMessage: string;
        escalationRules: string;
        maxCallDurationMinutes: number;
        transferPhone?: string;
      };
      status: string;
      createdAt: string;
    }>(`/agent/${agentId}`, {
      method: 'GET',
    });
  },

  /**
   * Partially update agent settings (e.g. transferPhone).
   */
  async updateAgentSettings(agentId: string, patch: Record<string, any>) {
    return apiCall<{ _id: string; settings: Record<string, any> }>(
      `/agent/${agentId}/settings`,
      { method: 'PATCH', body: JSON.stringify(patch) },
    );
  },

  /**
   * Delete (soft) an agent
   */
  async deleteAgent(agentId: string) {
    return apiCall<{ message: string }>(`/agent/${agentId}`, {
      method: 'DELETE',
    });
  },

  /**
   * Get user's serial number history
   */
  async getSerialNumbers() {
    return apiCall<Array<{
      serialNumber: string;
      status: string;
      agentId: string | null;
      phoneNumber: string | null;
      assignedAt: string | null;
      releasedAt: string | null;
    }>>('/agent/serial-numbers', {
      method: 'GET',
    });
  },

  /**
   * Get user's owned phone numbers (available for reuse)
   */
  async getOwnedPhoneNumbers() {
    return apiCall<Array<{
      _id: string;
      phoneNumber: string;
      name: string | null;
      provisionedAt: string | null;
      createdAt: string;
    }>>('/agent/phone-numbers', {
      method: 'GET',
    });
  },

  /**
   * Search available phone numbers via Telnyx
   */
  async searchNumbers(areaCode?: string) {
    const params = areaCode ? `?areaCode=${areaCode}` : '';
    return apiCall<string[]>(`/agent/search-numbers${params}`, {
      method: 'GET',
    });
  },

  /**
   * Get the empty template for an industry
   */
  async getTemplate(industry: string) {
    return apiCall<Record<string, any>>(`/agent/template/${industry}`, {
      method: 'GET',
    });
  },

  /**
   * Upload a document and extract business info into a template.
   * Sends multipart form data (file + industry field).
   */
  async extractFromDocument(file: File, industry: string) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('industry', industry);

    // Do NOT set Content-Type — let fetch set it with the boundary
    return apiCall<Record<string, any>>('/agent/extract', {
      method: 'POST',
      headers: {}, // override default JSON header
      body: formData as any,
    });
  },

  /**
   * Extract business information from a website URL
   */
  async extractFromUrl(url: string) {
    return apiCall<{
      success: boolean;
      extractedData: Record<string, any>;
      error?: string;
      conflicts?: Array<{ field: string; values: Array<{ source: string; value: any }> }>;
    }>('/agent/extract-from-url', {
      method: 'POST',
      body: JSON.stringify({ url }),
    });
  },

  /**
   * Generate a system prompt from a filled template
   */
  async generatePrompt(template: Record<string, any>, industry: string, language?: string) {
    return apiCall<{ systemPrompt: string }>('/agent/generate-prompt', {
      method: 'POST',
      body: JSON.stringify({ template, industry, language }),
    });
  },

  /**
   * List processed uploads (metadata only)
   */
  async listProcessedUploads(industry?: string) {
    const params = industry ? `?industry=${industry}` : '';
    return apiCall<Array<{
      _id: string;
      originalFileName: string;
      originalMimeType: string;
      originalSize: number;
      industry: string;
      status: string;
      processedAt?: string;
      createdAt: string;
      processedContent?: {
        pageCount?: number;
        imageCount?: number;
        processingMethod?: string;
      };
    }>>(`/agent/uploads${params}`, { method: 'GET' });
  },

  /**
   * Get latest processed upload (includes extracted data)
   */
  async getLatestProcessedUpload(industry?: string) {
    const params = industry ? `?industry=${industry}` : '';
    return apiCall<{
      _id: string;
      originalFileName: string;
      originalMimeType: string;
      originalSize: number;
      industry: string;
      status: string;
      processedAt?: string;
      createdAt: string;
      extractedData?: Record<string, any>;
    } | null>(`/agent/uploads/latest${params}`, { method: 'GET' });
  },

  /**
   * Get a processed upload by ID (includes extracted data)
   */
  async getProcessedUpload(uploadId: string) {
    return apiCall<{
      _id: string;
      originalFileName: string;
      originalMimeType: string;
      originalSize: number;
      industry: string;
      status: string;
      processedAt?: string;
      createdAt: string;
      extractedData?: Record<string, any>;
    }>(`/agent/uploads/${uploadId}`, { method: 'GET' });
  },

  /**
   * Worker status (online indicator)
   */
  async getWorkerStatus() {
    return apiCall<{
      online: boolean;
      lastSeenAt: string | null;
    }>('/agent/worker/status', { method: 'GET' });
  },

  /**
   * Get user's merged knowledge base (complete data source for their industry)
   */
  async getKnowledgeBase() {
    return apiCall<{
      _id: string;
      name: string;
      extractedData: Record<string, any>;
      processingStatus: string;
      processedAt?: string;
      createdAt: string;
    } | null>('/agent/knowledge-base', { method: 'GET' });
  },

  /**
   * Update the user's knowledge base. Merges patch into existing extractedData.
   * Applies to all active agents for this user.
   */
  async updateKnowledgeBase(patch: Record<string, any>) {
    return apiCall<{
      _id: string;
      name: string;
      extractedData: Record<string, any>;
      processingStatus: string;
      processedAt?: string;
      createdAt: string;
    }>('/agent/knowledge-base', { method: 'PATCH', body: JSON.stringify(patch) });
  },

  /**
   * Test NexHealth PMS connection credentials.
   * On success, also fetches and saves all static practice data to DB (user-level).
   */
  async testNexHealthConnection(subdomain: string, apiKey: string) {
    return apiCall<{
      success: boolean;
      locationId?: number;
      locationName?: string;
      practiceDataSynced?: boolean;
    }>(
      '/nexhealth/test-connection',
      {
        method: 'POST',
        body: JSON.stringify({ subdomain, apiKey }),
      },
    );
  },

  /**
   * Get saved NexHealth credentials for the current user (to pre-populate Step 2).
   * Returns connected=false if no credentials have been saved yet.
   */
  async getSavedNexHealthCredentials() {
    return apiCall<{
      connected: boolean;
      subdomain?: string;
      apiKey?: string;
      locationId?: number;
    }>('/nexhealth/saved-credentials');
  },

  async getSavedEpicCredentials() {
    return apiCall<{
      connected: boolean;
      orgName?: string;
      fhirBaseUrl?: string;
    }>('/medical/saved-credentials');
  },

  async saveEpicCredentials(payload: {
    orgName?: string;
    fhirBaseUrl: string;
  }) {
    return apiCall<{ connected: boolean }>('/medical/save-credentials', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  async testEpicConnection(fhirBaseUrl: string) {
    return apiCall<{ success: boolean; error?: string }>('/medical/test-connection', {
      method: 'POST',
      body: JSON.stringify({ fhirBaseUrl }),
    });
  },

  /**
   * Get merged KB + NexHealth practice info for Step 3 display (user-level, no agentId needed).
   */
  async getPracticeInfoPreview() {
    return apiCall<{
      kb: Record<string, any> | null;
      nexhealth: {
        locationName?: string;
        locationAddress?: string;
        locationPhone?: string;
        providers?: Array<{ id: number; name: string; providerType: string; npi?: string }>;
        appointmentTypes?: Array<{ id: number; name: string; duration: number }>;
        procedures?: Array<{ id: number; name: string; code?: string; duration?: number }>;
        insurancePlans?: Array<{ id: number; name: string; carrier_name?: string }>;
        subdomain?: string;
        syncedAt?: string | null;
      } | null;
    }>('/nexhealth/practice-info-preview');
  },

  /**
   * Preview static practice info from NexHealth credentials (no DB write)
   */
  async previewNexHealthPracticeInfo(
    subdomain: string,
    apiKey: string,
    locationId?: number,
  ) {
    return apiCall<{ success: boolean; locationId: number; practiceInfo: NexHealthPracticeSnapshot }>(
      '/nexhealth/preview-practice-info',
      {
        method: 'POST',
        body: JSON.stringify({ subdomain, apiKey, locationId }),
      },
    );
  },
};

/**
 * NexHealth dashboard API (testing mode — dental only)
 */
export interface NexHealthPatientRow {
  id: number;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  email: string;
  phone: string;
}

export interface NexHealthAppointmentRow {
  id: number;
  date: string;
  time: string;
  patientName: string;
  provider: string;
  type: string;
  status: string;
}

export interface NexHealthPracticeSnapshot {
  locationName?: string;
  locationAddress?: string;
  locationPhone?: string;
  providers?: Array<{ id: number; name: string; providerType: string; npi?: string }>;
  operatories?: Array<{ id: number; name: string }>;
  procedures?: Array<{ id: number; name: string; code?: string; duration?: number }>;
  appointmentTypes?: Array<{ id: number; name: string; duration: number }>;
  insurancePlans?: Array<{ id: number; name: string; carrier_name?: string }>;
  syncedAt?: string | null;
}

export interface NexHealthFinancialItem {
  id: number;
  date: string | null;
  description: string;
  amount: number;
}

export interface NexHealthFinancialRow {
  patientId: number;
  patientName: string;
  chargesTotal: number;
  paymentsTotal: number;
  adjustmentsTotal: number;
  balance: number;
  charges: NexHealthFinancialItem[];
  payments: NexHealthFinancialItem[];
  adjustments: NexHealthFinancialItem[];
}

export const nexhealthApi = {
  async listPatients(agentId: string) {
    return apiCall<{ patients: NexHealthPatientRow[] }>(
      `/nexhealth/patients?agentId=${encodeURIComponent(agentId)}`,
    );
  },
  async listAppointments(agentId: string, startDate?: string, endDate?: string) {
    const p = new URLSearchParams({ agentId });
    if (startDate) p.set('startDate', startDate);
    if (endDate) p.set('endDate', endDate);
    return apiCall<{ appointments: NexHealthAppointmentRow[] }>(
      `/nexhealth/appointments?${p}`,
    );
  },
  async listFinancials(agentId: string) {
    return apiCall<{ financials: NexHealthFinancialRow[] }>(
      `/nexhealth/financials?agentId=${encodeURIComponent(agentId)}`,
    );
  },
  async getPracticeOverview() {
    return apiCall<{ practiceInfo: NexHealthPracticeSnapshot | null }>(
      `/nexhealth/practice-overview`,
    );
  },
  async syncPracticeInfo(agentId?: string) {
    return apiCall<{ success: boolean; syncedAt: string; counts: Record<string, number> }>(
      `/nexhealth/sync-practice-info`,
      { method: 'POST', body: JSON.stringify(agentId ? { agentId } : {}) },
    );
  },
};

/**
 * Order API
 */
export interface OrderItem {
  name: string;
  quantity: number;
  specialInstructions?: string;
  unitPrice: number;
  subtotal: number;
}

export interface Order {
  id: string;
  agentId: string;
  userId: string;
  orderType: 'pickup' | 'delivery';
  status: 'pending' | 'confirmed' | 'preparing' | 'ready' | 'completed' | 'cancelled';
  customerName: string;
  customerPhone: string;
  deliveryAddress?: string | null;
  items: OrderItem[];
  estimatedTotal: number;
  finalTotal?: number | null;
  notes?: string | null;
  estimatedPickupTime?: string | null;
  createdAt: string;
  updatedAt: string;
  agent?: {
    id: string;
    name: string;
    phoneNumber: string;
  };
}

export const orderApi = {
  /**
   * List orders for the authenticated user (with optional filters)
   */
  async listOrders(params?: { status?: string; page?: number; limit?: number }) {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set('status', params.status);
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    const qs = searchParams.toString();
    return apiCall<{ orders: Order[]; total: number; page: number; limit: number }>(
      `/order${qs ? `?${qs}` : ''}`,
      { method: 'GET' },
    );
  },

  /**
   * Get a single order by ID
   */
  async getOrder(orderId: string) {
    return apiCall<Order>(`/order/${orderId}`, { method: 'GET' });
  },

  /**
   * Update order status
   */
  async updateOrderStatus(orderId: string, status: Order['status']) {
    return apiCall<Order>(`/order/${orderId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  },

  /**
   * Edit order details (final total, notes, items)
   */
  async updateOrder(orderId: string, data: {
    items?: OrderItem[];
    finalTotal?: number;
    notes?: string;
    estimatedPickupTime?: string;
  }) {
    return apiCall<Order>(`/order/${orderId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },
};

/**
 * Reservation API
 */
export interface RestaurantTable {
  id: string;
  agentId: string;
  userId: string;
  tableName: string;
  minCapacity: number;
  maxCapacity: number;
  features: string[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ReservationTableEntry {
  id: string;
  tableId: string;
  table: { id: string; tableName: string; maxCapacity: number; features: string[] };
}

export interface Reservation {
  id: string;
  agentId: string;
  userId: string;
  customerName: string;
  customerPhone: string;
  partySize: number;
  date: string;
  timeSlot: string;
  durationMinutes: number;
  status: 'pending' | 'confirmed' | 'seated' | 'completed' | 'cancelled' | 'no_show';
  notes?: string | null;
  specialRequests?: string | null;
  createdAt: string;
  updatedAt: string;
  agent?: { id: string; name: string };
  reservationTables: ReservationTableEntry[];
}

export const reservationApi = {
  /**
   * List reservations with optional filters
   */
  async listReservations(params?: { date?: string; status?: string; page?: number; limit?: number }) {
    const searchParams = new URLSearchParams();
    if (params?.date) searchParams.set('date', params.date);
    if (params?.status) searchParams.set('status', params.status);
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    const qs = searchParams.toString();
    return apiCall<{ reservations: Reservation[]; total: number; page: number; limit: number }>(
      `/reservation${qs ? `?${qs}` : ''}`,
      { method: 'GET' },
    );
  },

  /**
   * Get a single reservation
   */
  async getReservation(reservationId: string) {
    return apiCall<Reservation>(`/reservation/${reservationId}`, { method: 'GET' });
  },

  /**
   * Update reservation status
   */
  async updateReservationStatus(reservationId: string, status: Reservation['status']) {
    return apiCall<Reservation>(`/reservation/${reservationId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  },

  /**
   * Edit reservation details
   */
  async updateReservation(reservationId: string, data: { notes?: string; specialRequests?: string }) {
    return apiCall<Reservation>(`/reservation/${reservationId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  /**
   * List tables
   */
  async listTables(agentId?: string) {
    const params = agentId ? `?agentId=${agentId}` : '';
    return apiCall<RestaurantTable[]>(`/reservation/tables${params}`, { method: 'GET' });
  },

  /**
   * Get table configs from user's most recent agent (for pre-populating new agent creation)
   */
  async getPreviousTables() {
    return apiCall<{ tableName: string; minCapacity: number; maxCapacity: number; features: string[] }[]>(
      '/reservation/tables/previous',
      { method: 'GET' },
    );
  },

  /**
   * Create a table
   */
  async createTable(data: { agentId: string; tableName: string; minCapacity: number; maxCapacity: number; features?: string[] }) {
    return apiCall<RestaurantTable>('/reservation/tables', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Update a table
   */
  async updateTable(tableId: string, data: { tableName?: string; minCapacity?: number; maxCapacity?: number; features?: string[]; isActive?: boolean }) {
    return apiCall<RestaurantTable>(`/reservation/tables/${tableId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  /**
   * Delete a table
   */
  async deleteTable(tableId: string) {
    return apiCall<{ message: string }>(`/reservation/tables/${tableId}`, { method: 'DELETE' });
  },
};

/**
 * Call Log API
 */
export interface CallLogEntry {
  id: string;
  userId: string;
  agentId?: string | null;
  phoneNumberId: string;
  callId: string;
  livekitRoomName?: string | null;
  from: string;
  to: string;
  direction: 'inbound' | 'outbound';
  status: string;
  startedAt: string;
  answeredAt?: string | null;
  endedAt?: string | null;
  duration: number;
  transcript?: Array<{ role: string; content: string }> | null;
  summary?: string[] | null;
  functionsExecuted?: Array<Record<string, any>> | null;
  outcome?: string | null;
  callerName?: string | null;
  flagged: boolean;
  flagReason?: string | null;
  staffNotes?: string | null;
  staffReviewedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  agent?: { id: string; name: string; phoneNumber: string } | null;
  phoneNumber?: { id: string; phoneNumber: string; name: string } | null;
}

export interface CallStats {
  totalCalls: number;
  flaggedCalls: number;
  avgDuration: number;
  outcomes: Record<string, number>;
}

export const callLogApi = {
  async listCallLogs(params?: {
    startDate?: string;
    endDate?: string;
    outcome?: string;
    flagged?: boolean;
    page?: number;
    limit?: number;
  }) {
    const searchParams = new URLSearchParams();
    if (params?.startDate) searchParams.set('startDate', params.startDate);
    if (params?.endDate) searchParams.set('endDate', params.endDate);
    if (params?.outcome) searchParams.set('outcome', params.outcome);
    if (params?.flagged !== undefined) searchParams.set('flagged', String(params.flagged));
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    const qs = searchParams.toString();
    return apiCall<{ callLogs: CallLogEntry[]; total: number; page: number; limit: number }>(
      `/call-log${qs ? `?${qs}` : ''}`,
      { method: 'GET' },
    );
  },

  async getCallStats(params?: { startDate?: string; endDate?: string }) {
    const searchParams = new URLSearchParams();
    if (params?.startDate) searchParams.set('startDate', params.startDate);
    if (params?.endDate) searchParams.set('endDate', params.endDate);
    const qs = searchParams.toString();
    return apiCall<CallStats>(
      `/call-log/stats${qs ? `?${qs}` : ''}`,
      { method: 'GET' },
    );
  },

  async getCallLog(callLogId: string) {
    return apiCall<CallLogEntry>(`/call-log/${callLogId}`, { method: 'GET' });
  },

  async updateNotes(callLogId: string, data: { staffNotes?: string; markReviewed?: boolean }) {
    return apiCall<CallLogEntry>(`/call-log/${callLogId}/notes`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },
};

// ─── POS Integration API ──────────────────────────────────────────────────────

export interface POSCredentials {
  accessToken?: string; // Square
  apiKey?: string;      // Clover, Lightspeed
  locationId?: string;
  clientId?: string;
  clientSecret?: string;
  restaurantGuid?: string;
  merchantId?: string;
  accountId?: string;
  sandboxMode?: boolean;
}

export interface POSConfig {
  id: string;
  agentId: string;
  posSystem: 'square' | 'toast' | 'clover' | 'lightspeed';
  credentials: POSCredentials; // masked on GET
  locationId?: string;
  locationName?: string;
  isActive: boolean;
  lastMenuSyncAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface POSSyncStatus {
  posSystem: string;
  locationName?: string;
  isActive: boolean;
  lastMenuSyncAt?: string;
  menuItemCount: number;
  orderStats: {
    synced: number;
    failed: number;
    pending: number;
    total: number;
  };
}

export interface TestConnectionResult {
  success: boolean;
  locationName?: string;
  menuItemCount?: number;
  error?: string;
}

export interface POSLocationDetails {
  name: string;
  addressLine1?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  timezone?: string;
  phone?: string;
  hours?: Record<string, string>;
}

export interface POSKnowledgeResult {
  success: boolean;
  error?: string;
  location?: POSLocationDetails;
  menu?: {
    posSystem: string;
    locationName: string;
    categories: Array<{
      name: string;
      items: Array<{
        posItemId: string;
        name: string;
        price: number;
        available: boolean;
        modifiers?: Array<{ posModifierId: string; name: string; price: number }>;
      }>;
    }>;
    syncedAt: string;
  };
}

export const posApi = {
  async testConnection(posSystem: string, credentials: POSCredentials): Promise<TestConnectionResult> {
    return apiCall<TestConnectionResult>('/pos/test-connection', {
      method: 'POST',
      body: JSON.stringify({ posSystem, credentials }),
    });
  },

  async saveConfig(agentId: string, posSystem: string, credentials: POSCredentials): Promise<POSConfig> {
    return apiCall<POSConfig>('/pos/config', {
      method: 'POST',
      body: JSON.stringify({ agentId, posSystem, credentials }),
    });
  },

  async getConfig(agentId: string): Promise<POSConfig | null> {
    return apiCall<POSConfig | null>(`/pos/config/${agentId}`, { method: 'GET' });
  },

  async deleteConfig(agentId: string): Promise<void> {
    return apiCall<void>(`/pos/config/${agentId}`, { method: 'DELETE' });
  },

  async syncMenu(agentId: string): Promise<{ itemCount: number }> {
    return apiCall<{ itemCount: number }>(`/pos/sync-menu/${agentId}`, { method: 'POST' });
  },

  async getSyncStatus(agentId: string): Promise<POSSyncStatus | null> {
    return apiCall<POSSyncStatus | null>(`/pos/sync-status/${agentId}`, { method: 'GET' });
  },

  async fetchKnowledge(posSystem: string, credentials: POSCredentials): Promise<POSKnowledgeResult> {
    return apiCall<POSKnowledgeResult>('/pos/fetch-knowledge', {
      method: 'POST',
      body: JSON.stringify({ posSystem, credentials }),
    });
  },

  async retryOrderSync(orderId: string): Promise<void> {
    return apiCall<void>(`/pos/retry-order/${orderId}`, { method: 'POST' });
  },
};

// ---------------------------------------------------------------------------
// QA API
// ---------------------------------------------------------------------------

export interface QAAgent {
  id: string;
  userId: string;
  name: string;
  telnyxPhoneNumber: string;
  telnyxConfig?: Record<string, any>;
  livekitConfig?: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

export interface QATask {
  id: string;
  qaAgentId: string;
  name: string;
  targetAgentId: string;
  qaPlanPath: string;
  schedule?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  targetAgent?: { id: string; name: string; phoneNumber: string; industry: string };
  _count?: { sessions: number };
}

export interface QASession {
  id: string;
  qaTaskId: string;
  status: 'running' | 'completed' | 'failed' | 'stopped';
  startedAt: string;
  endedAt?: string;
  recordingPath?: string;
  transcriptPath?: string;
  reportPath?: string;
  overallScore?: number;
  passed?: boolean;
  errorMessage?: string;
  createdAt: string;
  qaTask?: {
    name: string;
    targetAgent?: { name: string; industry: string };
  };
}

export const qaApi = {
  async getAgent(): Promise<QAAgent | null> {
    return apiCall<QAAgent | null>('/qa/agent', { method: 'GET' });
  },

  async saveAgent(data: { name: string; telnyxPhoneNumber: string }): Promise<QAAgent> {
    return apiCall<QAAgent>('/qa/agent', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async listTasks(): Promise<QATask[]> {
    return apiCall<QATask[]>('/qa/tasks', { method: 'GET' });
  },

  async createTask(data: { name: string; targetAgentId: string; qaPlanPath: string; schedule?: string }): Promise<QATask> {
    return apiCall<QATask>('/qa/tasks', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateTask(id: string, data: { name?: string; schedule?: string; isActive?: boolean }): Promise<QATask> {
    return apiCall<QATask>(`/qa/tasks/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  async deleteTask(id: string): Promise<void> {
    return apiCall<void>(`/qa/tasks/${id}`, { method: 'DELETE' });
  },

  async runTask(id: string): Promise<{ sessionId: string; status: string }> {
    return apiCall<{ sessionId: string; status: string }>(`/qa/tasks/${id}/run`, { method: 'POST' });
  },

  async listSessions(page = 1, limit = 20): Promise<{ sessions: QASession[]; total: number; page: number; limit: number }> {
    return apiCall(`/qa/sessions?page=${page}&limit=${limit}`, { method: 'GET' });
  },

  async getSession(id: string): Promise<QASession> {
    return apiCall<QASession>(`/qa/sessions/${id}`, { method: 'GET' });
  },

  async listAllAgents(): Promise<Array<{ id: string; name: string; phoneNumber: string; industry: string; user: { businessName: string } }>> {
    return apiCall('/qa/agents', { method: 'GET' });
  },

  async listPlans(): Promise<Array<{ name: string; label: string }>> {
    return apiCall('/qa/plans', { method: 'GET' });
  },

  async getPlan(name: string): Promise<string> {
    const result = await apiCall<{ content: string }>(`/qa/plans/${encodeURIComponent(name)}`, { method: 'GET' });
    return result.content;
  },

  async savePlan(name: string, content: string): Promise<void> {
    return apiCall(`/qa/plans/${encodeURIComponent(name)}`, {
      method: 'PUT',
      body: JSON.stringify({ content }),
    });
  },

  async stopSession(id: string): Promise<QASession> {
    return apiCall<QASession>(`/qa/sessions/${id}/stop`, { method: 'POST' });
  },

  async getLiveTranscript(id: string): Promise<Array<{ speaker: string; text: string; timestamp: number }>> {
    return apiCall(`/qa/sessions/${id}/transcript`, { method: 'GET' });
  },
};

export type { ApiError };

// ── RAG API — Large Knowledge Expert Mode ─────────────────────────────────────

export interface RagDocument {
  id: string;
  filename: string;
  mimeType: string;
  fileSizeBytes: number;
  status: 'processing' | 'completed' | 'failed';
  processingStage: string | null;
  propCount: number | null;
  questionCount: number | null;
  raptorNodeCount: number | null;
  processingError: string | null;
  processedAt: string | null;
  createdAt: string;
}

export interface RagStatus {
  ready: boolean;
  docsTotal: number;
  docsCompleted: number;
  docsProcessing: number;
  docsError: number;
}

export const ragApi = {
  async uploadDocument(file: File): Promise<{ docId: string; status: string }> {
    const formData = new FormData();
    formData.append('file', file);
    const url = `${API_BASE_URL}/rag/upload`;
    const res = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ message: 'Upload failed' }));
      throw new Error(err.message || 'Upload failed');
    }
    return res.json();
  },

  async listDocuments(): Promise<RagDocument[]> {
    return apiCall<RagDocument[]>('/rag/documents', { method: 'GET' });
  },

  async getStatus(): Promise<RagStatus> {
    return apiCall<RagStatus>('/rag/status', { method: 'GET' });
  },

  async deleteDocument(docId: string): Promise<void> {
    return apiCall(`/rag/documents/${docId}`, { method: 'DELETE' });
  },
};
